/**
 * AccessRequestPage — a patient asks a clinic for a form, from the clinic's
 * own website (2026-10-23: the Disability Tax Credit for type 1 diabetes on
 * the Valley Metabolic Health site). Route: /request/:slug/:formKey.
 *
 * The page asks for the minimum (name, date of birth, an email or a mobile
 * number, and the code the form wants, such as the CRA reference number from
 * Part A), SEALS it in the browser under the clinic's public key and posts the
 * ciphertext (bridge/routers/access_requests.py): the request is unreadable to
 * the bridge and to the clinic website. The office reviews it before anything
 * is sent; the patient's link with the fee comes later, from the office.
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ParseoLogo from "../../shared/ui/ParseoLogo";
import { sealSubmission, sha256Hex } from "../../lib/bridgeClient";
import { consentHashInput } from "../../lib/paymentDisplay";
import {
  accessRequestPayload,
  accessRequestProblem,
  type AccessRequestInput,
} from "../../lib/accessRequest";

const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

interface PortalInfo {
  display_name: string;
  practice_name: string;
  form_label: string;
  code_label: string;
  code_required?: boolean;
  code_help?: string;
  pubkey_b64: string;
  /** Set when the request page saves a card (2026-10-24): the fee, and the
   *  notice and consent the patient link shows, worded for this form. */
  fee?: {
    total_cents: number;
    patient_fee_cents: number;
    physician_hst_cents: number;
    physician_display_name: string;
    notice: { text_blocks: string[]; consent_text: string; version: string };
  } | null;
}

const C = {
  ink: "#182a33", soft: "#5b6e7c", border: "#d8c9b6", bg: "#f5f0eb",
  teal: "#1b5e6e", red: "#c25450",
};

const input: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "11px 13px", fontSize: 15,
  borderRadius: 10, border: `1px solid ${C.border}`, outline: "none", background: "#fff",
};
const label: React.CSSProperties = {
  display: "block", textAlign: "left", fontSize: 13, color: C.soft, margin: "12px 0 4px",
};

const EMPTY: AccessRequestInput = {
  firstName: "", lastName: "", dob: "", email: "", phone: "", code: "", currentPatient: false,
};

export default function AccessRequestPage() {
  const { slug = "", formKey = "" } = useParams<{ slug: string; formKey: string }>();
  const [info, setInfo] = useState<PortalInfo | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable" | "sent" | "card">("loading");
  const [agreed, setAgreed] = useState(false);
  const [payToken, setPayToken] = useState<string | null>(null);
  const [form, setForm] = useState<AccessRequestInput>(EMPTY);
  const [trap, setTrap] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    fetch(`${BRIDGE_BASE_URL}/api/bridge/request-portal/${encodeURIComponent(slug)}/${encodeURIComponent(formKey)}`)
      .then(async (r) => {
        if (!live) return;
        if (!r.ok) { setState("unavailable"); return; }
        setInfo(await r.json());
        setState("ready");
      })
      .catch(() => { if (live) setState("unavailable"); });
    return () => { live = false; };
  }, [slug, formKey]);

  const set = (k: keyof AccessRequestInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: k === "currentPatient" ? e.target.checked : e.target.value });

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!info || busy) return;
    const problem = accessRequestProblem(form, new Date(), Boolean(info.code_required),
                                         `your ${info.code_label}`)
      || (info.fee && !agreed ? "Please read the fee notice and tick the box to agree." : null);
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError("");
    try {
      const sealed = sealSubmission(accessRequestPayload(form), info.pubkey_b64);
      const resp = await fetch(
        `${BRIDGE_BASE_URL}/api/bridge/request-portal/${encodeURIComponent(slug)}/${encodeURIComponent(formKey)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sealed_payload_b64: sealed, website: trap }),
        },
      );
      if (!resp.ok) {
        const detail = await resp.json().catch(() => null);
        setError(resp.status === 429 && detail?.detail
          ? String(detail.detail)
          : "Something went wrong. Please try again, or contact the clinic.");
        setBusy(false);
        return;
      }
      const sent = await resp.json().catch(() => ({}));
      setForm(EMPTY);
      if (info.fee && sent?.payment?.token) {
        setPayToken(String(sent.payment.token));
        setState("card");
        setBusy(false);
      } else {
        setState("sent");
      }
    } catch {
      setError("Something went wrong. Please try again, or contact the clinic.");
      setBusy(false);
    }
  }

  async function saveCard() {
    if (!info?.fee || !payToken || busy) return;
    setBusy(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json", "X-Request-Token": payToken };
      const consent = await fetch(`${BRIDGE_BASE_URL}/api/bridge/request-payment/consent`, {
        method: "POST", headers,
        body: JSON.stringify({
          version: info.fee.notice.version,
          notice_hash: await sha256Hex(consentHashInput(info.fee.notice)),
          total_cents: info.fee.total_cents,
        }),
      });
      if (!consent.ok) throw new Error("consent");
      const setup = await fetch(`${BRIDGE_BASE_URL}/api/bridge/request-payment/setup`, {
        method: "POST", headers,
      });
      if (!setup.ok) throw new Error("setup");
      const { pay_url } = await setup.json();
      // The secure payment page (Stripe), in this tab: nothing more to do here.
      window.location.href = pay_url;
    } catch {
      setError("The card step could not open. Your request was sent; the office can send you a payment link instead.");
      setBusy(false);
    }
  }

  const office = info ? (info.practice_name || `${info.display_name}'s office`) : "the clinic";

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center",
      backgroundColor: C.bg, padding: "32px 16px", boxSizing: "border-box",
    }}>
      <ParseoLogo size={44} />
      {state === "loading" && <p style={{ color: C.soft, marginTop: 18 }}>Loading…</p>}

      {state === "unavailable" && (
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          <h2 style={{ color: C.ink, margin: "16px 0 6px" }}>This request page is not available</h2>
          <p style={{ color: C.soft, lineHeight: 1.5 }}>
            Please contact the clinic directly to ask for your form.
          </p>
        </div>
      )}

      {state === "card" && info?.fee && (
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          {/* The fee notice the patient just agreed to is the one place the
              amount and the timing are stated (Michael, 2026-10-24,
              imp_f34132cf): this step does not repeat them. */}
          <h2 style={{ color: C.ink, margin: "16px 0 6px" }}>Request sent. One last step: save your card</h2>
          {error && (
            <p style={{ color: C.red, fontSize: 13, lineHeight: 1.45, margin: "10px 0 0" }}>{error}</p>
          )}
          <button onClick={() => { void saveCard(); }} disabled={busy} style={{
            marginTop: 14, width: "100%", padding: "12px 20px", borderRadius: 10, border: "none",
            background: C.teal, color: "#fff", fontSize: 15, fontWeight: 600,
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
          }}>
            {busy ? "Opening…" : "Save my card"}
          </button>
          <p style={{ fontSize: 12, color: C.soft, lineHeight: 1.5, margin: "12px 0 0" }}>
            The card is saved on Stripe’s secure page. Parseo and the clinic never see your
            card number.
          </p>
        </div>
      )}

      {state === "sent" && info && (
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          <h2 style={{ color: C.ink, margin: "16px 0 6px" }}>Request sent</h2>
          <p style={{ color: C.soft, lineHeight: 1.55 }}>
            {office} will review your request for the {info.form_label} and send you a secure
            link by email or text message. Nothing more is needed from you now.
          </p>
        </div>
      )}

      {state === "ready" && info && (
        <form onSubmit={submit} style={{ width: "100%", maxWidth: 420 }} noValidate>
          <h2 style={{ color: C.ink, margin: "16px 0 4px", textAlign: "center" }}>
            Request your {info.form_label}
          </h2>
          <p style={{ color: C.soft, lineHeight: 1.5, margin: "0 0 6px", textAlign: "center" }}>
            {info.display_name}{info.practice_name ? `, ${info.practice_name}` : ""}
          </p>

          <label style={label} htmlFor="ar-first">First name</label>
          <input id="ar-first" style={input} value={form.firstName} onChange={set("firstName")}
                 autoComplete="given-name" />
          <label style={label} htmlFor="ar-last">Last name</label>
          <input id="ar-last" style={input} value={form.lastName} onChange={set("lastName")}
                 autoComplete="family-name" />
          <label style={label} htmlFor="ar-dob">Date of birth</label>
          <input id="ar-dob" type="date" style={input} value={form.dob} onChange={set("dob")}
                 autoComplete="bday" />
          <label style={label} htmlFor="ar-email">Email</label>
          <input id="ar-email" type="email" style={input} value={form.email} onChange={set("email")}
                 autoComplete="email" />
          <label style={label} htmlFor="ar-phone">Mobile number</label>
          <input id="ar-phone" type="tel" style={input} value={form.phone} onChange={set("phone")}
                 autoComplete="tel" />
          <p style={{ fontSize: 12, color: C.soft, textAlign: "left", margin: "4px 0 0" }}>
            An email, a mobile number, or both: the office sends your secure link there.
          </p>
          {info.code_label && (
            <>
              <label style={label} htmlFor="ar-code">
                {info.code_label}{info.code_required ? "" : " (if you have it)"}
              </label>
              <input id="ar-code" style={input} value={form.code} onChange={set("code")}
                     autoComplete="off" />
              {info.code_help && (
                <p style={{ fontSize: 12, color: C.soft, textAlign: "left", margin: "4px 0 0",
                            lineHeight: 1.45 }}>
                  {info.code_help}
                </p>
              )}
            </>
          )}

          {/* A trap for bots: hidden from people and from screen readers. */}
          <div aria-hidden="true" style={{ position: "absolute", left: -10000, top: "auto",
                                           width: 1, height: 1, overflow: "hidden" }}>
            <label htmlFor="ar-website">Website</label>
            <input id="ar-website" tabIndex={-1} autoComplete="off" value={trap}
                   onChange={(e) => setTrap(e.target.value)} />
          </div>

          {info.fee && (
            <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 10, background: "#fff",
                          border: `1px solid ${C.border}`, textAlign: "left" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Fee notice</div>
              <div style={{ fontSize: 12, color: C.soft, lineHeight: 1.5, marginTop: 6,
                            maxHeight: 180, overflowY: "auto" }}>
                {info.fee.notice.text_blocks.map((b, i) => (
                  <p key={i} style={{ margin: "0 0 6px" }}>{b}</p>
                ))}
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13,
                              color: C.ink, marginTop: 8, lineHeight: 1.4 }}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                       style={{ marginTop: 3 }} />
                <span>{info.fee.notice.consent_text}</span>
              </label>
            </div>
          )}

          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", textAlign: "left",
                          fontSize: 14, color: C.ink, margin: "16px 0 0", lineHeight: 1.4 }}>
            <input type="checkbox" checked={form.currentPatient} onChange={set("currentPatient")}
                   style={{ marginTop: 3 }} />
            <span>I am a current patient of {office}.</span>
          </label>

          <p style={{ fontSize: 12, color: C.soft, lineHeight: 1.5, margin: "14px 0 0" }}>
            Your details are encrypted on this device and can be read only by {office}. They are
            not stored on the clinic's website. The office reviews each request before sending
            anything.
          </p>

          {error && (
            <p style={{ color: C.red, fontSize: 13, lineHeight: 1.45, margin: "10px 0 0" }}>{error}</p>
          )}
          <button type="submit" disabled={busy} style={{
            marginTop: 14, width: "100%", padding: "12px 20px", borderRadius: 10, border: "none",
            background: C.teal, color: "#fff", fontSize: 15, fontWeight: 600,
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
          }}>
            {busy ? "Sending…" : "Send my request"}
          </button>
        </form>
      )}
    </div>
  );
}
